import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DirectionsRailwayIcon from "@mui/icons-material/DirectionsRailway";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import NumbersIcon from "@mui/icons-material/Numbers";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

import api from "../../../services/api";
import CoachHistoryDialog from "../../History/CoachHistory/CoachHistoryDialog";

import CloseIcon from "@mui/icons-material/Close";
const initialForm = {
  coach_no: "",
  coach_type_code: "",
  railway_code: "",
  division_id: "",
  base_code: "",
  make_code: "",
  year_built: "",
  last_poh_date: "",
  last_out_date: "",
  repair_type_code: "",
  remark: "",
  status: "ACTIVE",
};

const initialNumberForm = {
  new_coach_no: "",
  effective_date: "",
  change_reason: "",
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-GB");
};

export default function CoachMaster() {
  const navigate = useNavigate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const rowsPerPage = isMobile ? 5 : 10;

  // =========================================================
  // LIST STATE
  // =========================================================

  const [rows, setRows] = useState([]);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);

  const [total, setTotal] = useState(0);

  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(false);

  // =========================================================
  // MASTER DATA
  // =========================================================

  const [coachTypes, setCoachTypes] = useState([]);

  const [railways, setRailways] = useState([]);

  const [makes, setMakes] = useState([]);

  const [repairTypes, setRepairTypes] = useState([]);

  // const [divisions, setDivisions] = useState([]);

  const [bases, setBases] = useState([]);

  const [mastersLoading, setMastersLoading] = useState(false);

  //  const [divisionLoading, setDivisionLoading] = useState(false);

  const [baseLoading, setBaseLoading] = useState(false);

  // =========================================================
  // History
  // =========================================================

  const [historyOpen, setHistoryOpen] = useState(false);
const [historyCoachNo, setHistoryCoachNo] = useState("");


  // =========================================================
  // ADD / EDIT
  // =========================================================

  const [openForm, setOpenForm] = useState(false);

  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState(initialForm);

  const [saving, setSaving] = useState(false);

  // =========================================================
  // CHANGE NUMBER
  // =========================================================

  const [openNumberDialog, setOpenNumberDialog] = useState(false);

  const [numberCoach, setNumberCoach] = useState(null);

  const [numberForm, setNumberForm] = useState(initialNumberForm);

  const [changingNumber, setChangingNumber] = useState(false);

  // =========================================================
  // DELETE
  // =========================================================

  const [deleteId, setDeleteId] = useState(null);

  const [deleting, setDeleting] = useState(false);

  // =========================================================
  // MESSAGE
  // =========================================================

  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const showMessage = (text, type = "success") => {
    setMessage({
      open: true,
      type,
      text,
    });
  };

  // =========================================================
  // LOAD COACHES
  // =========================================================

  const loadCoaches = useCallback(async () => {
    try {
      setLoading(true);

      const response = await api.get("/coaches", {
        params: {
          page,
          limit: rowsPerPage,
          search: search.trim(),
        },
      });

      const data = response.data;

      setRows(Array.isArray(data?.data) ? data.data : []);

      setTotal(Number(data?.pagination?.total || 0));

      setTotalPages(Math.max(Number(data?.pagination?.totalPages || 1), 1));
    } catch (error) {
      console.error("loadCoaches:", error);

      setRows([]);
      setTotal(0);
      setTotalPages(1);

      showMessage(
        error.response?.data?.message || "Failed to load coaches",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    loadCoaches();
  }, [loadCoaches]);

  // =========================================================
  // LOAD MASTER DATA
  // =========================================================

  const loadMasters = async () => {
    try {
      setMastersLoading(true);

      const response = await api.get("/coaches/masters");

      const data = response.data?.data || {};

      setCoachTypes(Array.isArray(data.coachTypes) ? data.coachTypes : []);

      setRailways(Array.isArray(data.railways) ? data.railways : []);

      setMakes(Array.isArray(data.makes) ? data.makes : []);

      setRepairTypes(Array.isArray(data.repairTypes) ? data.repairTypes : []);
    } catch (error) {
      console.error("loadMasters:", error);

      showMessage(
        error.response?.data?.message || "Failed to load master data",
        "error",
      );
    } finally {
      setMastersLoading(false);
    }
  };

  useEffect(() => {
    loadMasters();
  }, []);

  // =========================================================
  // LOAD DIVISIONS
  // =========================================================

  const loadBasesByRailway = async (railwayId) => {
    if (!railwayId) {
      setBases([]);
      return;
    }

    try {
      setBaseLoading(true);

      const response = await api.get(`/coaches/bases-by-railway/${railwayId}`);

      setBases(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error("loadBasesByRailway:", error);

      setBases([]);

      showMessage(
        error.response?.data?.message || "Failed to load coach bases",
        "error",
      );
    } finally {
      setBaseLoading(false);
    }
  };

  // =========================================================
  // FORM CHANGE
  // =========================================================

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  // =========================================================
  // COACH Code CHANGE
  // =========================================================

  const selectedCoachType = coachTypes.find(
    (item) => item.code === form.coach_type_code,
  );

  // =========================================================
  // RAILWAY CHANGE
  // =========================================================

  const handleRailwayChange = async (event) => {
    const railwayCode = event.target.value;

    setForm((prev) => ({
      ...prev,
      railway_code: railwayCode,
      division_id: "",
      base_code: "",
    }));

    setBases([]);

    const railway = railways.find((item) => item.code === railwayCode);

    if (railway?.id) {
      await loadBasesByRailway(railway.id);
    }
  };

  // =========================================================
  // DIVISION CHANGE
  // =========================================================

  const handleBaseChange = (event) => {
    const baseCode = event.target.value;

    const selectedBase = bases.find((item) => item.base_code === baseCode);

    setForm((prev) => ({
      ...prev,
      base_code: baseCode,
      division_id: selectedBase?.division_id || "",
    }));
  };

  // =========================================================
  // OPEN ADD
  // =========================================================

  const openAdd = () => {
    setEditingId(null);

    setForm(initialForm);

    //  setDivisions([]);
    setBases([]);

    setOpenForm(true);
  };

  // =========================================================
  // OPEN EDIT
  // =========================================================

  const openEdit = async (item) => {
    setEditingId(item.id);

    /*
     * First load the basic form data.
     * Division is NOT selected manually.
     * It will be derived from the selected Base.
     */

    setForm({
      coach_no: item.coach_no || "",

      coach_type_code: item.coach_type_code || "",

      railway_code: item.railway_code || "",

      division_id: item.division_id || "",

      base_code: item.base_code || "",

      make_code: item.make_code || "",

      year_built: item.year_built || "",

      last_poh_date: item.last_poh_date
        ? String(item.last_poh_date).slice(0, 10)
        : "",

      last_out_date: item.last_out_date
        ? String(item.last_out_date).slice(0, 10)
        : "",

      repair_type_code: item.repair_type_code || "",

      remark: item.remark || "",

      status: item.status || "ACTIVE",
    });

    /*
     * Open dialog immediately.
     */
    setOpenForm(true);

    /*
     * Load all Bases belonging to
     * the selected Railway.
     */
    const railway = railways.find((r) => r.code === item.railway_code);

    if (!railway?.id) {
      setBases([]);
      return;
    }

    try {
      const response = await api.get(`/coaches/bases-by-railway/${railway.id}`);

      const loadedBases = Array.isArray(response.data?.data)
        ? response.data.data
        : [];

      setBases(loadedBases);

      /*
       * Find the current Base.
       */
      const selectedBase = loadedBases.find(
        (base) => base.base_code === item.base_code,
      );

      /*
       * Division automatically comes
       * from the selected Base.
       */
      setForm((prev) => ({
        ...prev,

        division_id: selectedBase?.division_id || item.division_id || "",

        base_code: item.base_code || "",
      }));
    } catch (error) {
      console.error("Loading edit bases:", error);

      setBases([]);

      showMessage(
        error.response?.data?.message || "Failed to load coach bases",
        "error",
      );
    }
  };

  // =========================================================
  // SUBMIT ADD / EDIT
  // =========================================================

  const handleSubmit = async () => {
    const coachNo = form.coach_no.trim().toUpperCase();

    if (!coachNo) {
      showMessage("Coach number is required", "error");
      return;
    }

    if (!form.coach_type_code) {
      showMessage("Coach Code is required", "error");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        coach_no: coachNo,
        coach_type_code: form.coach_type_code,
        railway_code: form.railway_code || null,
        base_code: form.base_code || null,
        make_code: form.make_code || null,
        year_built: form.year_built || null,
        last_poh_date: form.last_poh_date || null,
        last_out_date: form.last_out_date || null,
        repair_type_code: form.repair_type_code || null,
        remark: form.remark.trim() || null,
        status: form.status,
      };

      if (editingId) {
        /*
         * Coach number is deliberately NOT sent
         * as a change during normal edit.
         */
        delete payload.coach_no;

        await api.put(`/coaches/${editingId}`, payload);

        showMessage("Coach updated successfully");
      } else {
        await api.post("/coaches", payload);

        showMessage("Coach registered successfully");
      }

      setOpenForm(false);

      setForm(initialForm);

      await loadCoaches();
    } catch (error) {
      console.error("handleSubmit:", error);

      showMessage(
        error.response?.data?.message || "Failed to save coach",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // OPEN CHANGE NUMBER
  // =========================================================

  const openChangeNumber = (item) => {
    setNumberCoach(item);

    setNumberForm({
      new_coach_no: "",
      effective_date: new Date().toISOString().slice(0, 10),
      change_reason: "",
    });

    setOpenNumberDialog(true);
  };

  // =========================================================
  // CHANGE NUMBER
  // =========================================================

  const handleChangeNumber = async () => {
    const newNumber = numberForm.new_coach_no.trim().toUpperCase();

    if (!newNumber) {
      showMessage("New coach number is required", "error");
      return;
    }

    if (!numberForm.effective_date) {
      showMessage("Effective date is required", "error");
      return;
    }

    try {
      setChangingNumber(true);

      await api.put(`/coaches/${numberCoach.id}/change-number`, {
        new_coach_no: newNumber,
        effective_date: numberForm.effective_date,
        change_reason: numberForm.change_reason.trim() || null,
      });

      setOpenNumberDialog(false);

      setNumberCoach(null);

      showMessage("Coach number changed successfully");

      await loadCoaches();
    } catch (error) {
      console.error("handleChangeNumber:", error);

      showMessage(
        error.response?.data?.message || "Failed to change coach number",
        "error",
      );
    } finally {
      setChangingNumber(false);
    }
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setDeleting(true);

      await api.delete(`/coaches/${deleteId}`);

      setDeleteId(null);

      showMessage("Coach deleted successfully");

      await loadCoaches();
    } catch (error) {
      console.error("handleDelete:", error);

      showMessage(error.response?.data?.message || "Delete failed", "error");
    } finally {
      setDeleting(false);
    }
  };

  // =========================================================
  // SEARCH
  // =========================================================

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        p: {
          xs: 1.5,
          sm: 2.5,
        },
      }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      <Box
        sx={{
          mb: 2.5,
          display: "flex",
          alignItems: {
            xs: "flex-start",
            sm: "center",
          },
          justifyContent: "space-between",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <DirectionsRailwayIcon
            sx={{
              fontSize: 32,
              color: "primary.main",
            }}
          />

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Coach Master
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Manage coach registration and current coach information.
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          disabled={mastersLoading}
        >
          Add Coach
        </Button>
      </Box>

      {/* =====================================================
          MAIN CARD
      ===================================================== */}

      <Card
        sx={{
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
          borderRadius: 2.5,
        }}
      >
        <CardContent
          sx={{
            p: {
              xs: 1.5,
              sm: 2,
            },
          }}
        >
          {/* SEARCH + COUNT */}

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              mb: 2,
            }}
          >
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={handleSearch}
              placeholder="Search coach no., type, railway, base, make..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Chip
              label={`${total} Records`}
              color="warning"
              variant="outlined"
              sx={{
                flexShrink: 0,
              }}
            />
          </Box>

          {/* =================================================
              LOADING
          ================================================= */}

          {loading ? (
            <Box
              sx={{
                minHeight: 250,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box
              sx={{
                py: 6,
                textAlign: "center",
              }}
            >
              <Typography color="text.secondary">
                {search.trim()
                  ? "No matching coaches found."
                  : "No coaches found."}
              </Typography>
            </Box>
          ) : isMobile ? (
            /* =================================================
               MOBILE
            ================================================= */

            <Stack spacing={1.5}>
              {rows.map((item) => (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        fontWeight={700}
                        sx={{
                          wordBreak: "break-word",
                        }}
                      >
                        {item.coach_no}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        {item.coach_type_name || item.coach_type_code || "-"}
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      label={item.status || "ACTIVE"}
                      color={item.status === "ACTIVE" ? "success" : "default"}
                    />
                  </Box>

                  <Divider sx={{ my: 1.2 }} />

                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>AC/NON-AC:</strong> {item.ac_nonac || "-"}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Railway:</strong>{" "}
                      {item.railway_code || item.railway_name || "-"}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Base:</strong>{" "}
                      {item.base_code || item.base_name || "-"}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Make:</strong>{" "}
                      {item.make_code || item.make_name || "-"}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Year Built:</strong> {item.year_built || "-"}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Last POH:</strong>{" "}
                      {formatDate(item.last_poh_date)}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Repair:</strong>{" "}
                      {item.repair_type_code || item.repair_type_name || "-"}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={0.5}
                    sx={{ mt: 1 }}
                  >
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(item)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Change Number">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openChangeNumber(item)}
                      >
                        <NumbersIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="History">
                      {/* <IconButton
                        size="small"
                        onClick={() => {
                          navigate(
                            `/coach-history?coachNo=${encodeURIComponent(
                              item.coach_no,
                            )}`,
                          );
                        }}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton> */}


<IconButton
  size="small"
  onClick={() => {
    setHistoryCoachNo(item.coach_no);
    setHistoryOpen(true);
  }}
>
  <HistoryIcon fontSize="small" />
</IconButton>


                    </Tooltip>

                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteId(item.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            /* =================================================
               DESKTOP TABLE
            ================================================= */

            <TableContainer
              sx={{
                width: "100%",
                maxWidth: "100%",
                overflowX: "auto",
              }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: 1250,
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell>Coach No.</TableCell>

                    <TableCell>Coach Code</TableCell>

                    <TableCell>AC/NON-AC</TableCell>

                    <TableCell>Railway</TableCell>

                    <TableCell>Base</TableCell>

                    <TableCell>Make</TableCell>

                    <TableCell>Year Built</TableCell>

                    <TableCell>Last POH</TableCell>

                    <TableCell>Repair Type</TableCell>

                    <TableCell>Status</TableCell>

                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.coach_no}
                      </TableCell>

                      <TableCell>
                        {item.coach_type_code || item.coach_type_name || "-"}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={item.ac_nonac}
                          size="small"
                          color={item.ac_nonac === "AC" ? "primary" : "default"}
                        />
                      </TableCell>

                      <TableCell>
                        {item.railway_code || item.railway_name || "-"}
                      </TableCell>

                      <TableCell>
                        {item.base_code || item.base_name || "-"}
                      </TableCell>

                      <TableCell>
                        {item.make_code || item.make_name || "-"}
                      </TableCell>

                      <TableCell>{item.year_built || "-"}</TableCell>

                      <TableCell>{formatDate(item.last_poh_date)}</TableCell>

                      <TableCell>
                        {item.repair_type_code || item.repair_type_name || "-"}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={item.status}
                          color={
                            item.status === "ACTIVE" ? "success" : "default"
                          }
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(item)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Change Number">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openChangeNumber(item)}
                          >
                            <NumbersIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="History">
                          {/* <IconButton
                            size="small"
                            onClick={() => {
                              navigate(
                                `/coach-history?coachNo=${encodeURIComponent(
                                  item.coach_no,
                                )}`,
                              );
                            }}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton> */}

                          <IconButton
  size="small"
  onClick={() => {
    setHistoryCoachNo(item.coach_no);
    setHistoryOpen(true);
  }}
>
  <HistoryIcon fontSize="small" />
</IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteId(item.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* =================================================
              PAGINATION
          ================================================= */}

          {!loading && total > 0 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                count={totalPages}
                page={Math.min(page, totalPages)}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size={isMobile ? "small" : "medium"}
                showFirstButton={!isMobile}
                showLastButton={!isMobile}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* =====================================================
          ADD / EDIT DIALOG
      ===================================================== */}

      <Dialog
        open={openForm}
        onClose={() => !saving && setOpenForm(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingId ? "Edit Coach" : "Register New Coach"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {/* Coach Number */}

            <TextField
              label="Coach Number"
              value={form.coach_no}
              onChange={handleChange("coach_no")}
              fullWidth
              required
              disabled={Boolean(editingId)}
              helperText={
                editingId
                  ? "Use Change Number action to change the coach number."
                  : "Enter the current coach number."
              }
            />

            {/* Coach Code */}

            <FormControl fullWidth required>
              <InputLabel>Coach Code</InputLabel>

              <Select
                value={form.coach_type_code}
                label="Coach Code"
                onChange={handleChange("coach_type_code")}
              >
                {coachTypes.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {/* {item.name} ({item.code}) */}
                    {item.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* AC / NON-AC */}

            <TextField
              label="AC / NON-AC"
              value={selectedCoachType?.ac_nonac || ""}
              fullWidth
              disabled
              helperText="Taken from Basic Coach Code and cannot be changed here."
            />

            {/* Railway */}

            <FormControl fullWidth>
              <InputLabel>Railway</InputLabel>

              <Select
                value={form.railway_code}
                label="Railway"
                onChange={handleRailwayChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>

                {railways.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.name} ({item.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Coach Base */}

            {/* Coach Base */}

            <FormControl fullWidth>
              <InputLabel>Coach Base</InputLabel>

              <Select
                value={form.base_code}
                label="Coach Base"
                onChange={handleBaseChange}
                disabled={!form.railway_code || baseLoading}
              >
                <MenuItem value="">
                  <em>{baseLoading ? "Loading..." : "Select Base"}</em>
                </MenuItem>

                {bases.map((item) => (
                  <MenuItem key={item.base_code} value={item.base_code}>
                    {item.base_name} ({item.base_code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Division - Derived From Base */}

            <TextField
              label="Division"
              value={
                bases.find((item) => item.base_code === form.base_code)
                  ?.division_name || ""
              }
              fullWidth
              disabled
              helperText="Automatically derived from selected Coach Base"
            />

            {/* Base */}

            {/* Make */}

            <FormControl fullWidth>
              <InputLabel>Make</InputLabel>

              <Select
                value={form.make_code}
                label="Make"
                onChange={handleChange("make_code")}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>

                {makes.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.name} ({item.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Year Built */}

            <TextField
              label="Year Built"
              type="number"
              value={form.year_built}
              onChange={handleChange("year_built")}
              fullWidth
              inputProps={{
                min: 1900,
                max: new Date().getFullYear(),
              }}
            />

            {/* Dates */}

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={2}
            >
              <TextField
                label="Last POH Date"
                type="date"
                value={form.last_poh_date}
                onChange={handleChange("last_poh_date")}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                label="Last Out Date"
                type="date"
                value={form.last_out_date}
                onChange={handleChange("last_out_date")}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Stack>

            {/* Repair Type */}

            <FormControl fullWidth>
              <InputLabel>Repair Type</InputLabel>

              <Select
                value={form.repair_type_code}
                label="Repair Type"
                onChange={handleChange("repair_type_code")}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>

                {repairTypes.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.name} ({item.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Remark */}

            <TextField
              label="Remark"
              value={form.remark}
              onChange={handleChange("remark")}
              fullWidth
              multiline
              minRows={3}
            />

            {/* Status */}

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>

              <Select
                value={form.status}
                label="Status"
                onChange={handleChange("status")}
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>

                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenForm(false)} disabled={saving}>
            Cancel
          </Button>

          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : editingId ? "Update" : "Register Coach"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          CHANGE NUMBER DIALOG
      ===================================================== */}

      <Dialog
        open={openNumberDialog}
        onClose={() => !changingNumber && setOpenNumberDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <SwapHorizIcon color="primary" />
            Change Coach Number
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Current Coach Number"
              value={numberCoach?.coach_no || ""}
              fullWidth
              disabled
            />

            <TextField
              label="New Coach Number"
              value={numberForm.new_coach_no}
              onChange={(event) =>
                setNumberForm((prev) => ({
                  ...prev,
                  new_coach_no: event.target.value,
                }))
              }
              fullWidth
              required
              autoFocus
            />

            <TextField
              label="Effective Date"
              type="date"
              value={numberForm.effective_date}
              onChange={(event) =>
                setNumberForm((prev) => ({
                  ...prev,
                  effective_date: event.target.value,
                }))
              }
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="Change Reason"
              value={numberForm.change_reason}
              onChange={(event) =>
                setNumberForm((prev) => ({
                  ...prev,
                  change_reason: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={2}
              placeholder="Reason for coach number change"
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenNumberDialog(false)}
            disabled={changingNumber}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<NumbersIcon />}
            onClick={handleChangeNumber}
            disabled={changingNumber}
          >
            {changingNumber ? "Changing..." : "Change Number"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          DELETE DIALOG
      ===================================================== */}

      <Dialog
        open={Boolean(deleteId)}
        onClose={() => !deleting && setDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Coach?</DialogTitle>

        <DialogContent>
          <Typography>Are you sure you want to delete this coach?</Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Coaches with number or conversion history cannot be physically
            deleted.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={message.open}
        autoHideDuration={4000}
        onClose={() =>
          setMessage((prev) => ({
            ...prev,
            open: false,
          }))
        }
      >
        <Alert
          severity={message.type}
          variant="filled"
          onClose={() =>
            setMessage((prev) => ({
              ...prev,
              open: false,
            }))
          }
        >
          {message.text}
        </Alert>
      </Snackbar>



<CoachHistoryDialog
  coachNo={historyCoachNo}
  open={historyOpen}
  onClose={() => setHistoryOpen(false)}
/>


    </Box>
  );
}
